package com.elan.edubridge.ui.staff

import com.elan.edubridge.shared.di.SharedGraph
import com.elan.edubridge.shared.domain.model.CommandFailure
import com.elan.edubridge.shared.domain.model.ContentReport
import com.elan.edubridge.shared.domain.model.ContentReportResolution
import com.elan.edubridge.shared.domain.model.ReadResult
import com.elan.edubridge.shared.domain.model.ContentReportTargetType
import com.elan.edubridge.shared.domain.model.Permission
import com.elan.edubridge.shared.domain.model.SessionState
import com.elan.edubridge.ui.shell.Router
import com.elan.edubridge.ui.shell.escapeHtml
import com.elan.edubridge.ui.shell.formatTimestamp
import com.elan.edubridge.ui.shell.icon
import com.elan.edubridge.ui.shell.partialFailureHtml
import com.elan.edubridge.ui.shell.readErrorHtml
import com.elan.edubridge.ui.shell.wireRetry
import kotlinx.browser.document
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLFormElement
import org.w3c.dom.HTMLSelectElement
import org.w3c.dom.HTMLTextAreaElement

/** EDITOR 이상만 보는 게시판 신고 대기열. UID는 어떤 행에도 렌더하지 않는다. */
object ContentReportsPage {
    private val scope = MainScope()

    /** 읽기 실패·부분 실패 양쪽의 "다시 시도" 버튼 id(#30). */
    private const val RETRY_ID = "content-reports-retry"
    private val repository = SharedGraph.createRepository()
    private val commandRepository = SharedGraph.createAcademyCommandRepository()

    fun open(session: SessionState, academyId: String) {
        if (!session.activePermission.atLeast(Permission.EDITOR)) {
            Router.replace("/app/$academyId/today")
            return
        }
        scope.launch {
            render(session, academyId, repository.contentReportsResult(academyId, openOnly = true))
        }
    }

    private fun render(session: SessionState, academyId: String, result: ReadResult<ContentReport>) {
        DashboardShell.render(session, academyId, "reports", pageHtml(result)) {
            wireRetry(RETRY_ID) { open(session, academyId) }
            wireQueue(session, academyId, result.items)
        }
    }

    /**
     * 신고 큐가 비어 보이는 것과 못 읽는 것은 **모더레이션에서 특히 다르다**(#30) — 전자는
     * "처리할 게 없다", 후자는 "밀린 신고가 있는지 모른다"이다. 같은 화면을 그리면 안 된다.
     */
    private fun pageHtml(result: ReadResult<ContentReport>): String {
        val reports = result.items
        val body = when {
            result.isEmptySuccess ->
                """<div class="eb-empty-state">${icon("icon-check", "xl")}<h2>접수된 신고가 없어요</h2><p>새 신고가 접수되면 이곳에서 검토할 수 있어요.</p></div>"""
            reports.isEmpty() -> readErrorHtml(result.failure ?: CommandFailure.INTERNAL, RETRY_ID)
            else -> reports.mapIndexed { index, report -> reportHtml(index, report) }.joinToString("")
        }
        val partialBanner = if (result.isPartialFailure) partialFailureHtml(RETRY_ID) else ""
        return """
            <div class="eb-page-header">
                <div>
                    <h1 class="eb-page-title">신고 관리</h1>
                    <p class="eb-meta">접수된 게시판 신고를 검토하고 처리해 주세요.</p>
                </div>
            </div>
            $partialBanner
            <section class="eb-card" aria-label="열린 신고 목록">$body</section>
        """.trimIndent()
    }

    private fun reportHtml(index: Int, report: ContentReport): String {
        val targetLabel = when (report.targetType) {
            ContentReportTargetType.POST -> "게시글"
            ContentReportTargetType.COMMENT -> "댓글"
            ContentReportTargetType.USER -> "작성자"
        }
        val canOpenTarget = report.targetType != ContentReportTargetType.USER &&
            (report.postId != null || report.targetType == ContentReportTargetType.POST)
        return """
            <article class="eb-notice-item">
                ${icon("icon-warning", "md")}
                <div>
                    <strong>${targetLabel.escapeHtml()} 신고 · ${report.reason.displayName.escapeHtml()}</strong>
                    <span class="eb-meta">${formatTimestamp(report.createdAt)}${if (report.reportCount > 1) " · 동일 신고 ${report.reportCount}회" else ""}</span>
                    ${report.detail?.takeIf { it.isNotBlank() }?.let { "<p>${it.escapeHtml()}</p>" }.orEmpty()}
                </div>
                <div class="eb-notice-actions">
                    ${if (canOpenTarget) """<button class="eb-button eb-button--ghost eb-button--sm content-report-open-target" type="button" data-report-index="$index">대상 확인</button>""" else ""}
                    <button class="eb-button eb-button--primary eb-button--sm content-report-resolve" type="button" data-report-index="$index">처리</button>
                </div>
            </article>
        """.trimIndent()
    }

    private fun wireQueue(session: SessionState, academyId: String, reports: List<ContentReport>) {
        val targetButtons = document.querySelectorAll(".content-report-open-target")
        for (index in 0 until targetButtons.length) {
            val button = targetButtons.item(index) as? HTMLElement ?: continue
            button.onclick = {
                reports.getOrNull(button.getAttribute("data-report-index")?.toIntOrNull() ?: -1)?.let { report ->
                    BoardPage.openReportedTarget(session, academyId, report)
                }
                null
            }
        }
        val resolveButtons = document.querySelectorAll(".content-report-resolve")
        for (index in 0 until resolveButtons.length) {
            val button = resolveButtons.item(index) as? HTMLElement ?: continue
            button.onclick = {
                reports.getOrNull(button.getAttribute("data-report-index")?.toIntOrNull() ?: -1)?.let { report ->
                    openResolutionEditor(session, academyId, report)
                }
                null
            }
        }
    }

    private fun openResolutionEditor(session: SessionState, academyId: String, report: ContentReport) {
        val backdrop = document.createElement("div") as HTMLElement
        backdrop.className = "eb-modal-backdrop"
        backdrop.innerHTML = """
            <div class="eb-modal" role="dialog" aria-modal="true" aria-labelledby="content-report-resolution-title">
                <header class="eb-modal__header">
                    <h2 id="content-report-resolution-title">신고 처리</h2>
                    <button class="eb-icon-button" id="content-report-resolution-close" type="button" aria-label="닫기">${icon("icon-close", "md")}</button>
                </header>
                <form class="eb-modal__body" id="content-report-resolution-form">
                    <div class="eb-field">
                        <label for="content-report-resolution">처리 결과</label>
                        <select id="content-report-resolution">
                            <option value="ACTIONED">조치 완료</option>
                            <option value="DISMISSED">문제 없음</option>
                        </select>
                    </div>
                    <div class="eb-field">
                        <label for="content-report-resolution-note">처리 메모 <span class="eb-meta">(선택)</span></label>
                        <textarea id="content-report-resolution-note" maxlength="500" rows="4" placeholder="운영진 참고용 메모입니다."></textarea>
                    </div>
                    <p class="eb-form-error" id="content-report-resolution-error" role="alert" hidden></p>
                </form>
                <footer class="eb-modal__footer">
                    <button class="eb-button eb-button--ghost" id="content-report-resolution-cancel" type="button">취소</button>
                    <button class="eb-button eb-button--primary" id="content-report-resolution-submit" type="submit" form="content-report-resolution-form">처리 완료</button>
                </footer>
            </div>
        """.trimIndent()
        document.body?.appendChild(backdrop)

        var submitting = false
        fun close() {
            if (!submitting) backdrop.remove()
        }
        fun setSubmitting(value: Boolean) {
            submitting = value
            listOf(
                "#content-report-resolution-close",
                "#content-report-resolution-cancel",
                "#content-report-resolution-submit",
            ).forEach { selector ->
                (backdrop.querySelector(selector) as? HTMLButtonElement)?.disabled = value
            }
        }
        fun showError(message: String) {
            (backdrop.querySelector("#content-report-resolution-error") as? HTMLElement)?.let { error ->
                error.textContent = message
                error.removeAttribute("hidden")
            }
        }

        (backdrop.querySelector("#content-report-resolution-close") as? HTMLElement)?.onclick = { close(); null }
        (backdrop.querySelector("#content-report-resolution-cancel") as? HTMLElement)?.onclick = { close(); null }
        backdrop.onclick = { event -> if (event.target == backdrop) close(); null }
        (backdrop.querySelector("#content-report-resolution-form") as? HTMLFormElement)?.onsubmit = { event ->
            event.preventDefault()
            if (!submitting) {
                val resolutionName = (backdrop.querySelector("#content-report-resolution") as? HTMLSelectElement)?.value
                val resolution = ContentReportResolution.entries.firstOrNull { it.name == resolutionName }
                val note = (backdrop.querySelector("#content-report-resolution-note") as? HTMLTextAreaElement)?.value?.trim()
                if (resolution == null) {
                    showError("처리 결과를 선택해 주세요.")
                } else if ((note?.length ?: 0) > 500) {
                    showError("처리 메모는 500자 이하로 입력해 주세요.")
                } else {
                    setSubmitting(true)
                    scope.launch {
                        val result = commandRepository.resolveContentReport(
                            academyId,
                            report.id,
                            resolution,
                            note?.takeIf { it.isNotBlank() },
                        )
                        if (result.isSuccess) {
                            backdrop.remove()
                            Toast.success("신고를 처리했어요.")
                            open(session, academyId)
                        } else {
                            showError(result.errorMessage ?: "신고를 처리하지 못했어요. 다시 시도해 주세요.")
                            setSubmitting(false)
                        }
                    }
                }
            }
            null
        }
        (backdrop.querySelector("#content-report-resolution") as? HTMLSelectElement)?.focus()
    }

}
